import { Component, HostListener, inject, OnInit, ViewChild } from '@angular/core';
import { Title } from '@angular/platform-browser';
import { BehaviorSubject, firstValueFrom, Subject, Subscription } from 'rxjs';
import { CustomModalComponent } from 'src/app/components/custom-modal/custom-modal.component';
import { AuthenticationContants } from 'src/app/constants/authentication-constants';
import { ProjectName } from 'src/app/constants/project-name-constants';
import { TableAction } from 'src/app/interfaces/custom-table/custom-table-action.interface';
import { ColumnResponseModel } from 'src/app/models/responses/column-response.model';
import { PagedResponseModel } from 'src/app/models/responses/paged-response.model';
import { PurchaseProjectionTableModel } from 'src/app/models/tables/preliminaries/purchase-projection-table.model';
import { WindowService } from 'src/app/services/authentication/window.service';
import { ConfirmDialogService } from 'src/app/services/confirm-dialog/confirm-dialog.service';
import { HeaderService } from 'src/app/services/header/header.service';
import { PurchaseProjectionService } from 'src/app/services/preliminaries/purchase-projection.service';
import { ToastHandlerService } from 'src/app/services/toast/toast-handler.service';
import { RegisterPurchaseProjectionComponent } from '../register-purchase-projection/register-purchase-projection.component';


@Component({
  selector: 'app-purchase-projection',
  templateUrl: './purchase-projection.component.html',
  styleUrls: ['./purchase-projection.component.scss'],
  standalone: false
})
export class PurchaseProjectionComponent implements OnInit {

  private readonly confirmDialogService = inject(ConfirmDialogService);
  private readonly headerService = inject(HeaderService);
  private readonly purchaseProjectionService = inject(PurchaseProjectionService);
  private readonly title = inject(Title);
  private readonly toastHandlerService = inject(ToastHandlerService);
  private readonly windowService = inject(WindowService);

  @ViewChild('registerPurchaseProjectionModal') registerPurchaseProjectionModalComponent?: CustomModalComponent | null = null;
  ascendant: boolean = true;
  columnSort: string | null = null;
  componentRegisterPurchaseProjection = RegisterPurchaseProjectionComponent;
  data: PagedResponseModel<PurchaseProjectionTableModel> = new PagedResponseModel<PurchaseProjectionTableModel>();
  filters: ColumnResponseModel[] = [];
  header: ColumnResponseModel[] = [];
  inputsForRegisterModals$ = new BehaviorSubject<any>({
    purchaseProjectionId: 0,
    permissionUpdatable: false
  });
  isOpened: string | undefined = undefined;
  limit: number = 10;
  nameWindow: string = 'Proyección Compra';
  output$ = new Subject<any>();
  page: number = 1;
  permissionUpdatable: boolean = false;
  projectName = ProjectName.projectName;
  purchaseProjectionId: number = 0;
  subscription: Subscription;
  tableActions: TableAction[] = [
    {
      label: 'Editar',
      icon: 'pi pi-pencil',
      action: 'edit'
    },
    {
      label: 'Eliminar',
      icon: 'pi pi-trash',
      action: 'delete'
    },
  ];
  url: string = 'purchase-projection';

  async ngOnInit() {
    this.title.setTitle(`${this.nameWindow} - ${this.projectName}`);
    this.headerService.setTitle(this.nameWindow);
    const filters = localStorage.getItem(`${this.url}`);
    if (filters) this.filters = JSON.parse(filters);
    this.getPermission();
    await this.getInitialData();
    this.subscription = this.output$.subscribe(async ({ type, payload }) => {
      switch (type) {
        case 'onReload': {
          await this.onReload(payload);
          break;
        }
        default: {
          console.warn('Tipo de evento desconocido:', type);
        }
      }
    });
  }

  @HostListener('window:beforeunload')
  beforeUnloadHandler() {
    this.ngOnDestroy();
  }

  ngOnDestroy() {
    const windowsOpenedJson = localStorage.getItem(AuthenticationContants.keyWindowsOpenedLocalStorage);
    if (windowsOpenedJson) {
      const windowsOpened: Array<string> = JSON.parse(windowsOpenedJson);
      const window = windowsOpened.findIndex(x => x === this.url);
      if (window != -1) {
        windowsOpened.splice(window, 1);
        localStorage.setItem(AuthenticationContants.keyWindowsOpenedLocalStorage, JSON.stringify(windowsOpened));
      }
    }
  }

  createNewRegister() {
    this.purchaseProjectionId = 0;
    this.openRegisterPurchaseProjectionModal();
  }

  async deletePurchaseProjection(purchaseProjection: PurchaseProjectionTableModel) {
    if (!this.permissionUpdatable) {
      this.toastHandlerService.showToast(
        'warning',
        `El usuario no tiene permisos para eliminar proyecciones de compra. Comuníquese con el administrador del sistema.`
      );
      return;
    }
    const accepted = await this.confirmDialogService.confirm(`¿Realmente desea eliminar la proyección de compra?`);
    if (accepted) {
      try {
        await firstValueFrom(this.purchaseProjectionService.deletePurchaseProjection(purchaseProjection.purchaseProjectionId));
        await this.getAllPurchaseProjectionsWithPagination();
        this.toastHandlerService.showToast(
          'success',
          'La proyección de compra fue eliminada exitosamente.'
        );
      } catch (error) {
        console.error('Error al eliminar la proyección de compra.', error);
      }
    }
  }

  async exportGeneralConsultation() {
    try {
      const response = await firstValueFrom(this.purchaseProjectionService.exportGeneralConsultation(this.filters, this.columnSort, this.ascendant));
      const blob = response;
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = 'Proyección Compra.xlsx';
      a.click();
      window.URL.revokeObjectURL(url);
    } catch (error) {
      console.error('Error al exportar la proyección de compra.', error);
    }
  }

  async getAllPurchaseProjectionsWithPagination() {
    try {
      const response = await firstValueFrom(this.purchaseProjectionService.getAllPurchaseProjectionsWithPagination(this.page, this.limit,
        this.columnSort, this.ascendant, this.filters));
      this.data = response;
    } catch (error) {
      console.error('Error al buscar las proyecciones de compra.', error);
    }
  }

  async getColumnsForGeneralConsultation() {
    try {
      if (this.header.length === 0) {
        const response = await firstValueFrom(this.purchaseProjectionService.getColumnsForGeneralConsultation());
        this.header = response;
      }
    } catch (error) {
      console.error('Error al buscar las columnas para la consulta general de las proyecciones de compra.', error);
    }
  }

  async getInitialData() {
    await Promise.all([
      this.getColumnsForGeneralConsultation(),
      this.getAllPurchaseProjectionsWithPagination(),
    ]);
  }

  async getPermission() {
    const windowsOpenedJson = localStorage.getItem(AuthenticationContants.keyWindowsOpenedLocalStorage);
    if (windowsOpenedJson) {
      const windowsOpened: Array<string> = JSON.parse(windowsOpenedJson);
      this.isOpened = windowsOpened.find(x => x === this.url);
      windowsOpened.push(this.url);
      localStorage.setItem(AuthenticationContants.keyWindowsOpenedLocalStorage, JSON.stringify(windowsOpened));
      if (this.isOpened) {
        this.toastHandlerService.showToast(
          'error',
          `No se permite tener dos ventanas/pestañas de ${this.nameWindow} abiertas.`
        );
      }
    } else {
      localStorage.clear();
    }
    await this.getPermissionProfileByUrl();
  }

  async getPermissionProfileByUrl() {
    try {
      const response = await firstValueFrom(this.windowService.getPermissionProfileByUrl(this.url));
      this.permissionUpdatable = response;
      this.updateInputs();
    } catch (error) {
      console.error('Error al buscar los permisos del perfil.', error);
    }
  }

  async onAction(event: { action: string, item?: PurchaseProjectionTableModel, selectedItems?: number[] }) {
    if (event.action === 'edit' && event.item) {
      this.purchaseProjectionId = event.item.id;
      this.openRegisterPurchaseProjectionModal();
    };

    if (event.action === 'delete' && event.item) {
      this.deletePurchaseProjection(event.item);
    }
  }

  async onExport() {
    await this.exportGeneralConsultation();
  }

  async onFilterChange(event: { filters: any }) {
    const filters = event.filters.map((value: any, index: any): any => ({ value, index }))
      .sort((a: any, b: any) => b.index - a.index).map((item: any) => item.value);
    this.filters = this.removeDuplicates(filters);
    this.filters.forEach((filter: any) => this.processFilter(filter));
  }

  async onPageChange(event: { page: number; pageSize: number; sortField?: string; sortOrder: number }) {
    this.page = event.page;
    this.limit = event.pageSize;
    this.columnSort = event.sortField ?? null;
    this.ascendant = event.sortOrder === 1;
    await this.getAllPurchaseProjectionsWithPagination();
  }

  async onReload(reload: boolean) {
    if (reload) {
      await this.getAllPurchaseProjectionsWithPagination();
    }
  }

  openRegisterPurchaseProjectionModal() {
    this.updateInputs();
    this.registerPurchaseProjectionModalComponent?.show();
  }

  processFilter(filter: any) {
    const { propertyName, valueFilter, valueAlterFilter } = filter;
    if (valueFilter === null || valueFilter === undefined || valueFilter === '' || valueFilter === 0) {
      this.filters = this.filters.filter(f => f.propertyName !== propertyName);
    } else {
      const existingFilter = this.filters.find(f => f.propertyName === propertyName);
      if (existingFilter) {
        existingFilter.valueFilter = valueFilter;
        existingFilter.valueAlterFilter = valueAlterFilter;
      }
    }
  }

  removeDuplicates(filters: any[]): any[] {
    return filters.filter((value, index, self) =>
      index === self.findIndex((t: any) => t.propertyName === value.propertyName)
    );
  }

  updateInputs() {
    this.inputsForRegisterModals$.next({
      purchaseProjectionId: this.purchaseProjectionId,
      permissionUpdatable: this.permissionUpdatable
    });
  }
}
